'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLangDataStore } from '@/utils/util_states';
import { PuzzleThemes, type PuzzleThemeCode } from '@/datas/ConstData';
import type { PuzzleTab } from '@/utils/util_types';

// ── Filter configuration ──────────────────────────────

type FilterGroup = {
    key: string;
    chips: { value: string; label: string }[];
    /** Whether only one chip in this group can be active at a time */
    exclusive?: boolean;
};

// ── Types ─────────────────────────────────────────────

export type ActiveFilters = {
    sort: PuzzleTab;
    themes: PuzzleThemeCode[];
    origin: string[];
    difficulty: string[];
    sortBy: string;
};

type FilterChipsProps = {
    activeFilters: ActiveFilters;
    onFilterChange: (filters: ActiveFilters) => void;
};

// ── Component ─────────────────────────────────────────

export function FilterChips({ activeFilters, onFilterChange }: FilterChipsProps) {
    const { langData } = useLangDataStore();
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const themeLabels: Record<PuzzleThemeCode, string> = useMemo(() => ({
        honkaku: langData.theme_honkaku,
        henkaku: langData.theme_henkaku,
        hardcore: langData.theme_hardcore,
        quirky: langData.theme_quirky,
        suspense: langData.theme_suspense,
        casual: langData.theme_casual,
        horror: langData.theme_horror,
        heartwarming: langData.theme_heartwarming,
    }), [langData]);

    const filterGroups: FilterGroup[] = useMemo(() => [
        {
            key: 'sort',
            exclusive: true,
            chips: [
                { value: 'latest', label: langData.discover_latest },
                { value: 'hot-today', label: langData.discover_hot_today },
                { value: 'hot-week', label: langData.discover_hot_week },
            ],
        },
        {
            key: 'themes',
            exclusive: false,
            chips: PuzzleThemes.map((theme) => ({ value: theme, label: themeLabels[theme] })),
        },
        {
            key: 'origin',
            exclusive: false,
            chips: [
                { value: 'original', label: langData.puzzle_card_original },
                { value: 'repost', label: langData.puzzle_card_repost },
                { value: 'ai', label: 'AI' },
            ],
        },
        {
            key: 'difficulty',
            exclusive: false,
            chips: [
                { value: 'easy', label: langData.discover_difficulty_easy },
                { value: 'medium', label: langData.discover_difficulty_medium },
                { value: 'hard', label: langData.discover_difficulty_hard },
            ],
        },
    ], [langData, themeLabels]);

    const sortOptions = useMemo(() => [
        { value: 'composite', label: langData.discover_sortby_composite },
        { value: 'newest', label: langData.discover_sortby_newest },
        { value: 'most-liked', label: langData.discover_sortby_most_liked },
        { value: 'most-played', label: langData.discover_sortby_most_played },
    ], [langData]);

    const updateScrollState = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        setCanScrollLeft(scroller.scrollLeft > 2);
        setCanScrollRight(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 2);
    }, []);

    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const frame = requestAnimationFrame(updateScrollState);
        const observer = new ResizeObserver(updateScrollState);
        observer.observe(scroller);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [filterGroups, updateScrollState]);

    function handleChipClick(groupKey: string, value: string, exclusive?: boolean) {
        const next = { ...activeFilters };

        if (groupKey === 'sort') {
            next.sort = value as PuzzleTab;
        } else {
            const key = groupKey as 'themes' | 'origin' | 'difficulty';
            const arr = [...next[key]];
            const idx = arr.indexOf(value);

            if (idx >= 0) {
                arr.splice(idx, 1);
            } else {
                if (exclusive) {
                    // Replace
                    arr.length = 0;
                    arr.push(value);
                } else {
                    arr.push(value);
                }
            }
            if (key === 'themes') next.themes = arr as PuzzleThemeCode[];
            else next[key] = arr;
        }

        onFilterChange(next);
    }

    function handleSortByChange(e: React.ChangeEvent<HTMLSelectElement>) {
        onFilterChange({ ...activeFilters, sortBy: e.target.value });
    }

    function isActive(groupKey: string, value: string): boolean {
        if (groupKey === 'sort') return activeFilters.sort === value;
        const key = groupKey as 'themes' | 'origin' | 'difficulty';
        if (key === 'themes') return activeFilters.themes.includes(value as PuzzleThemeCode);
        return activeFilters[key].includes(value);
    }

    function scrollFilters(direction: -1 | 1) {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        scroller.scrollBy({
            left: direction * Math.min(scroller.clientWidth * 0.8, 640),
            behavior: 'smooth',
        });
    }

    return (
        <div className="px-4 pt-4 sm:px-10">
            <div className="mx-auto flex max-w-[1200px] items-center gap-2">
                <div
                    ref={scrollerRef}
                    onScroll={updateScrollState}
                    data-testid="filter-scroller"
                    className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                    {filterGroups.map((group, groupIndex) => (
                        <React.Fragment key={group.key}>
                            {groupIndex > 0 && <div className="mx-2 h-5 w-px shrink-0 bg-border" />}
                            {group.chips.map((chip) => (
                                <button
                                    type="button"
                                    key={chip.value}
                                    onClick={() => handleChipClick(group.key, chip.value, group.exclusive)}
                                    data-filter-group={group.key}
                                    data-filter-value={chip.value}
                                    className={cn(
                                        'shrink-0 border px-4 py-[7px] text-xs font-medium tracking-normal',
                                        'cursor-pointer transition-all duration-200',
                                        isActive(group.key, chip.value)
                                            ? 'border-lamp/20 bg-lamp-dim text-lamp'
                                            : 'border-border bg-transparent text-muted-foreground hover:text-foreground/60',
                                    )}
                                >
                                    {chip.label}
                                </button>
                            ))}
                        </React.Fragment>
                    ))}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                    <button
                        type="button"
                        onClick={() => scrollFilters(-1)}
                        disabled={!canScrollLeft}
                        aria-label="Scroll filters left"
                        title="Previous filters"
                        className="flex h-8 w-8 items-center justify-center border border-border text-muted-foreground transition-colors hover:border-lamp/30 hover:text-foreground disabled:cursor-default disabled:opacity-25"
                    >
                        <ChevronLeft size={15} />
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollFilters(1)}
                        disabled={!canScrollRight}
                        aria-label="Scroll filters right"
                        title="Next filters"
                        className="flex h-8 w-8 items-center justify-center border border-border text-muted-foreground transition-colors hover:border-lamp/30 hover:text-foreground disabled:cursor-default disabled:opacity-25"
                    >
                        <ChevronRight size={15} />
                    </button>
                </div>

                <div className="relative shrink-0">
                    <select
                        value={activeFilters.sortBy}
                        onChange={handleSortByChange}
                        aria-label={langData.discover_sortby_composite}
                        className={cn(
                            'h-8 appearance-none border border-border bg-card py-[7px] pl-3 pr-7 text-xs text-muted-foreground',
                            'max-w-[112px] cursor-pointer outline-none sm:max-w-none',
                            'font-[family-name:var(--font-noto)] transition-colors hover:border-lamp/30',
                        )}
                    >
                        {sortOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    <ChevronDown
                        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                        size={12}
                    />
                </div>
            </div>
        </div>
    );
}

export const defaultFilters: ActiveFilters = {
    sort: 'latest',
    themes: [],
    origin: [],
    difficulty: [],
    sortBy: 'composite',
};
